import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
} from '@radix-ui/themes';
import {
  DialogButton,
  DialogPopoverButton,
} from 'components/common/dialogs/button';
import { ScrollShadow } from 'components/common/scroll-shadow';
import { t } from 'i18next';
import { IFullDialog } from 'interfaces/i-dialogs';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useEffect, useRef, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';
import { v4 } from 'uuid';

const DEBUG_BORDERS = false;

export const CommonDialog = (props: IFullDialog) => {
  const [open, setOpen] = useState(true);

  // track open dialog(s)
  useEffect(() => {
    if (props.ignore) {
      return;
    }

    const id = v4();

    useMiscStore.getState().registerDialog({
      identifier: props.identifier,
      v4ID: id,
      open: true,
    });

    return () => {
      useMiscStore.getState().registerDialog({
        identifier: props.identifier,
        v4ID: id,
        open: false,
      });
    };
  }, [props.ignore, props.identifier]);

  // trigger parent's onClose fn
  useEffect(() => {
    if (open) {
      return;
    }

    props.onClose();
  }, [open]);

  const isFlexing = !!props.vFlexHeight;

  const hasFooter =
    props.leftFooter ||
    (props.buttons ?? []).filter((b) => !b.invisible).length > 0;

  const themeWrapper = useRef(document.getElementById('ThemeWrapper'));

  return (
    <Dialog.Root
      open={open}
      // called when closed via Esc key
      onOpenChange={(value) => {
        setOpen(value);
        props.onOpenChange?.(value);
      }}
      // allows interaction with other elements on the screen while open (e.g. toasts on the side)
      modal={false}
    >
      <Dialog.Portal container={themeWrapper.current}>
        <div
          className="DialogOverlay"
          onClick={() => {
            if (props.modal) {
              // while in modal mode (default), clicking on the overlay closes the dialog
              return;
            }

            const IS_TOUCH_DEVICE = navigator.maxTouchPoints > 0;
            if (IS_TOUCH_DEVICE) {
              // prevents interactions with inputs in the dialog from accidentally closing things
              return;
            }

            props.onClose();
          }}
        ></div>

        <Dialog.Content
          aria-describedby={props.description}
          className={[
            'DialogContent',
            isFlexing ? RADIX.VFLEX.COLUMN : '',
          ].join(' ')}
          style={{
            // force width to match max
            width: props.width,
            maxWidth: props.maxWidth || RADIX.DIALOG.WIDTH.MAX,
            minWidth: RADIX.DIALOG.WIDTH.MIN,

            height: props.vFlexHeight,
            maxHeight: RADIX.DIALOG.HEIGHT.MAX,
            minHeight: RADIX.DIALOG.HEIGHT.MIN,

            overflowX: 'hidden',
            overflowY: 'auto',
            border: DEBUG_BORDERS ? '1px solid red' : undefined,
          }}
          // avoid dismissing when clicking on toasts
          onInteractOutside={(e) => e.preventDefault()}
        >
          <Flex
            direction="column"
            gap="5"
            style={{
              height: isFlexing ? '100%' : undefined,
              border: DEBUG_BORDERS ? '1px solid green' : undefined,
            }}
          >
            <Dialog.Title className="DialogTitle">
              <Flex pt={RADIX.FLEX.PAD.XS} justify="between">
                <Flex gap={RADIX.FLEX.GAP.SM} minWidth="0">
                  <Box minWidth="0">
                    {typeof props.title === 'string' ? (
                      <Heading
                        size={RADIX.HEADING.SIZE.MODAL_TITLE}
                        mb="0"
                        truncate
                      >
                        {t(props.title)}
                      </Heading>
                    ) : (
                      props.title
                    )}

                    {props.subtitle}
                  </Box>

                  {props.loading && (
                    <Box className="valign-center" pb="1">
                      <Spinner size="3" />
                    </Box>
                  )}
                </Flex>

                <Flex gap="2">
                  {props.titleActions}

                  <IconButton
                    color={RADIX.COLOR.NEUTRAL}
                    variant="soft"
                    className="btn-floating"
                    onClick={props.onClose}
                  >
                    <Cross2Icon />
                  </IconButton>
                </Flex>
              </Flex>
            </Dialog.Title>

            {props.description && (
              <Dialog.Description>
                <Text color={RADIX.COLOR.NEUTRAL}>{props.description}</Text>
              </Dialog.Description>
            )}

            {isFlexing ? (
              <ScrollShadow
                style={{
                  // allow this flex child to shrink so it scrolls internally
                  // instead of pushing the footer below the fold
                  flexGrow: 1,
                  minHeight: 0,
                  border: DEBUG_BORDERS ? '1px solid yellow' : undefined,
                }}
              >
                {props.content}
              </ScrollShadow>
            ) : (
              <Box
                style={{
                  border: DEBUG_BORDERS ? '1px solid yellow' : undefined,
                }}
              >
                {props.content}
              </Box>
            )}

            {hasFooter && (
              <Flex gap={RADIX.FLEX.GAP.SM} justify="between">
                <Box>{props.leftFooter}</Box>

                <Flex gap={RADIX.FLEX.GAP.SM}>
                  {props.buttons
                    ?.filter((b) => !b.invisible)
                    .map((b, i) =>
                      b.popoverProps ? (
                        <DialogPopoverButton key={i} {...b} />
                      ) : (
                        <DialogButton key={i} {...b} />
                      )
                    )}
                </Flex>
              </Flex>
            )}
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
